import React from "react";
import { FormattedNumber } from "react-intl";
import { compress } from "image-conversion";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Dialog } from "@syos-music/common";

import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  Slider,
  FormHelperText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

/**
 * Convert a byte size to human readable format
 */

export function getHumanFileSize(size) {
  let unitIdx = 0;
  const units = ["B", "kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  while (size > 1024 && unitIdx < units.length) {
    size /= 1014;
    unitIdx++;
  }

  return Math.max(size, 0.1).toFixed(1) + " " + units[unitIdx];
}

async function getCroppedImageBlob(imageEl, pixelCrop, quality, resizeWidth) {
  const x = parseInt((pixelCrop.x * imageEl.naturalWidth) / 100, 10);
  const y = parseInt((pixelCrop.y * imageEl.naturalHeight) / 100, 10);
  const width = parseInt((pixelCrop.width * imageEl.naturalWidth) / 100, 10);
  const height = parseInt((pixelCrop.height * imageEl.naturalHeight) / 100, 10);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");

  ctx.drawImage(imageEl, x, y, width, height, 0, 0, width, height);

  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob(resolve, "image/jpeg")
  );

  const config = {
    quality,
    width: resizeWidth > 0 ? resizeWidth : width,
    height: resizeWidth > 0 ? parseInt((resizeWidth / width) * height) : height,
  };

  return {
    blob: blob ? await compress(blob, config) : blob,
    size: { width: config.width, height: config.height },
  };
}

export default function ImageCropper({
  file,
  defaultCrop,
  defaultResizeWidth,
  onCrop,
  children,
}) {
  const defaultInputs = {
    imageEl: null,
    imageFileSize: NaN,
    pixelCrop: defaultCrop,
    quality: 0.92,
    resizeWidth: defaultResizeWidth,
  };

  // Modal stuff
  const [isOpen, setIsOpen] = React.useState(false);

  const open = () => setIsOpen(true);
  const close = () => {
    setIsOpen(false);
    setCrop(defaultCrop);
    updateInputs(defaultInputs);
  };

  const [submitting, setSubmitting] = React.useState(false);

  const [crop, setCrop] = React.useState(defaultCrop);

  const [inputs, setInputs] = React.useState(defaultInputs);

  const updateInputs = async (values) => {
    const newInputs = { ...inputs, ...values };

    if (values.imageEl) {
      const imageFileSize = await fetch(values.imageEl.src)
        .then((res) => res.blob())
        .then((blob) => blob.size);

      Object.assign(newInputs, { imageFileSize });
    }

    setInputs(newInputs);
    updateCropped(newInputs);
  };

  const [cropped, setCropped] = React.useState({
    blob: { size: NaN },
    url: null,
    size: { width: 0, height: 0 },
  });

  const updateCropped = async ({
    imageEl,
    pixelCrop,
    quality,
    resizeWidth,
  }) => {
    if (!imageEl || !pixelCrop || !quality) return;

    const { blob, size } = await getCroppedImageBlob(
      imageEl,
      pixelCrop,
      quality,
      resizeWidth
    );

    if (blob) {
      URL.revokeObjectURL(cropped.url);
      const url = URL.createObjectURL(blob);

      setCropped({ blob, url, size });
    }
  };

  return (
    <>
      {children({ isOpen, open, close })}

      <Dialog disableEnforceFocus open={isOpen} onClose={close}>
        <Dialog.Title color="primary">Modifier une image</Dialog.Title>

        <Dialog.Content>
          <Grid container>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                minHeight: "60vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  alignSelf: "stretch",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReactCrop
                  src={file.url + "?s3cors"}
                  onImageLoaded={async (imageEl) => updateInputs({ imageEl })}
                  crop={crop}
                  onChange={setCrop}
                  onComplete={async (crop, pixelCrop) => {
                    if (!inputs.imageEl || !crop.width || !crop.height) return;
                    updateInputs({ pixelCrop });
                  }}
                  crossorigin="anonymous"
                  styles={{
                    padding: 1,
                    backgroundColor: "transparent",
                  }}
                />
              </Box>

              {inputs.imageEl && (
                <>
                  <Typography sx={{ mt: 1 }}>
                    {inputs.imageEl.naturalWidth}&nbsp;&times;&nbsp;
                    {inputs.imageEl.naturalHeight}&nbsp; (
                    <FormattedNumber
                      value={inputs.imageFileSize}
                      // eslint-disable-next-line react/style-prop-object
                      style="unit"
                      unit="byte"
                      unitDisplay="narrow"
                    />
                    )<small>({getHumanFileSize(inputs.imageFileSize)})</small>
                  </Typography>
                </>
              )}
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                minHeight: "60vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Grid container>
                <Grid item xs={12} md={6}>
                  <TextField
                    type="number"
                    min={1}
                    step={1}
                    value={inputs.resizeWidth}
                    onChange={(evt) => {
                      const resizeWidth = parseFloat(evt.target.value) || 0;
                      updateInputs({ resizeWidth });
                    }}
                    label="Largeur de l’image finale"
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <FormLabel
                      styles={{
                        transform: "translate(0, 1.5px) scale(0.75)",
                        transformOrigin: "top left",
                      }}
                    >
                      Qualité de compression
                    </FormLabel>

                    <Slider
                      value={inputs.quality}
                      onChange={(evt, quality) => updateInputs({ quality })}
                      min={0}
                      max={1}
                      step={0.01}
                    />

                    <Grid container spacing={0}>
                      <Grid item xs={2}>
                        <FormHelperText component="div">
                          <Typography
                            align="left"
                            variant="inherit"
                            display="block"
                          >
                            0
                          </Typography>
                        </FormHelperText>
                      </Grid>

                      <Grid item xs>
                        <FormHelperText component="div">
                          <Typography
                            align="center"
                            variant="inherit"
                            display="block"
                          >
                            {inputs.quality.toFixed(2)}
                          </Typography>
                        </FormHelperText>
                      </Grid>

                      <Grid item xs={2}>
                        <FormHelperText component="div">
                          <Typography
                            align="right"
                            variant="inherit"
                            display="block"
                          >
                            1
                          </Typography>
                        </FormHelperText>
                      </Grid>
                    </Grid>
                  </FormControl>
                </Grid>
              </Grid>

              <Box
                sx={{
                  marginTop: 2,
                  flexGrow: 1,
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  position: "relative",
                }}
              >
                {cropped.url && (
                  <Box
                    component="img"
                    src={cropped.url}
                    alt=""
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      maxWidth: "100%",
                      maxHeight: "100%",
                    }}
                  />
                )}
              </Box>

              <Typography sx={{ mt: 1 }}>
                {cropped.size.width}&nbsp;&times;&nbsp;
                {cropped.size.height}&nbsp;
                <small>({getHumanFileSize(cropped.blob.size)})</small>
              </Typography>
            </Grid>
          </Grid>
        </Dialog.Content>

        <Dialog.Actions>
          <Button variant="text" onClick={close}>
            Annuler
          </Button>

          <LoadingButton
            loading={submitting}
            onClick={async () => {
              setSubmitting(true);

              try {
                await onCrop(cropped.blob);
                setSubmitting(false);
                close();
              } catch (err) {
                setSubmitting(false);
                throw err;
              }
            }}
          >
            Enregistrer
          </LoadingButton>
        </Dialog.Actions>
      </Dialog>
    </>
  );
}

ImageCropper.defaultProps = {
  defaultCrop: { unit: "%", x: 0, y: 0, width: 100, height: 100 },
  defaultResizeWidth: 0,
  onCrop: () => {},
};
